import { useCreateLocation } from 'api/locations/hooks';
import { useFetchRetailersInfinite } from 'api/retailers/hooks';
import { Form, ModalForm, PopupContext } from 'components';
import VanityUrlAvailabilityInput from 'components/Form/VanityUrlAvailabilityInput';
import { ErrorFeedback } from 'components/Form/styles';
import { useField, useFormikContext } from 'formik';
import { debounce, uniqBy } from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CreatableSelect from 'react-select/creatable';
import styled from 'styled-components';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { media } from 'utils/rwd';
import validationSchemas from './schemas';

const RETAILER_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 400;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InviteOwnerEmailSelect = ({ name }) => {
  const { popupRoot } = useContext(PopupContext);
  const { values } = useFormikContext();
  const [field, meta, { setValue, setTouched }] = useField(name);
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const previousRegionUid = useRef(values.regionUid);

  const {
    data,
    isLoading: isLoadingRetailers,
    isFetching: isFetchingRetailers,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchRetailersInfinite({
    pageSize: RETAILER_PAGE_SIZE,
    email: search,
    filters: { region: values.regionUid },
  });

  const debouncedSetSearch = useMemo(
    () => debounce((value) => setSearch(value), SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(
    () => () => {
      debouncedSetSearch.cancel();
    },
    [debouncedSetSearch],
  );

  useEffect(() => {
    if (previousRegionUid.current !== values.regionUid) {
      setValue('');
      setInputValue('');
      setSearch('');
      debouncedSetSearch.cancel();
      previousRegionUid.current = values.regionUid;
    }
  }, [debouncedSetSearch, setValue, values.regionUid]);

  const options = useMemo(() => {
    const retailers = data?.pages?.flatMap((page) => page.results || []) || [];
    const retailerOptions = retailers
      .filter((retailer) => retailer.email)
      .map((retailer) => ({
        value: retailer.email,
        label: retailer.email,
      }));

    return uniqBy(retailerOptions, 'value');
  }, [data]);

  const selectedValue = field.value
    ? options.find((option) => option.value === field.value) || {
        value: field.value,
        label: field.value,
      }
    : null;

  const commitEmail = (email) => {
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      return false;
    }

    setValue(normalizedEmail);
    setTouched(true);
    setInputValue('');
    setSearch('');
    debouncedSetSearch.cancel();
    return EMAIL_REGEX.test(normalizedEmail);
  };

  const handleInputChange = (value, { action }) => {
    if (action !== 'input-change') {
      return inputValue;
    }

    setInputValue(value);
    debouncedSetSearch(value);
    return value;
  };

  const handleBlur = () => {
    setTouched(true);
    if (inputValue) {
      commitEmail(inputValue);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && inputValue) {
      commitEmail(inputValue);
      event.preventDefault();
    }
  };

  const handleMenuScrollToBottom = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const isInvalid = meta.touched && meta.error;
  const isLoading = isLoadingRetailers || isFetchingRetailers || isFetchingNextPage;

  return (
    <>
      <CreatableSelect
        {...field}
        closeMenuOnSelect
        filterOption={() => true}
        formatCreateLabel={(value) => value}
        inputValue={inputValue}
        isClearable
        isLoading={isLoading}
        isValidNewOption={(value) => EMAIL_REGEX.test(value.trim())}
        menuPortalTarget={popupRoot || undefined}
        onBlur={handleBlur}
        onChange={(option) => {
          setValue(option?.value || '');
          setInputValue('');
          setSearch('');
          debouncedSetSearch.cancel();
        }}
        onCreateOption={commitEmail}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        options={options}
        value={selectedValue}
        styles={{
          control: (provided) => ({
            ...provided,
            minHeight: '44.39px',
            height: 'auto',
            textAlign: 'left',
            ...(isInvalid ? { border: '1px solid red' } : {}),
          }),
          menu: (provided) => ({
            ...provided,
            zIndex: 1032,
            overflow: 'visible',
          }),
          menuPortal: (provided) => ({
            ...provided,
            zIndex: 1032,
          }),
        }}
        className={`text-left ${isInvalid && 'is-invalid'}`}
      />
      {isInvalid ? <ErrorFeedback>{meta.error}</ErrorFeedback> : null}
    </>
  );
};

const AddLocationForm = ({ regions }) => {
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'locations' });
  const { mutate: createLocation, isPending: isCreatingLocation } = useCreateLocation();
  const selectedRegion = useSelectedProfileStore((state) => state.selectedRegionUid);
  const currentRegion = useMemo(
    () => regions.find((region) => region.uid === selectedRegion) || regions?.[0],
    [regions, selectedRegion],
  );
  const initialValues = {
    name: '',
    description: '',
    regionUid: currentRegion?.uid ?? '',
    regionName: currentRegion?.name ?? '',
    vanityUrl: '',
    location_tracking_id: '',
    inviteOwnerEmail: '',
  };
  const validationSchema = useMemo(() => validationSchemas.location_create, []);

  const handleSubmit = ({
    name,
    description,
    regionUid,
    vanityUrl,
    location_tracking_id,
    inviteOwnerEmail,
  }) => {
    createLocation({
      name,
      description,
      regionUid,
      vanityUrl,
      location_tracking_id,
      inviteOwnerEmail,
    });
  };

  const buildDomain = (uid) => {
    const region = regions.find((r) => r.uid === uid);
    return region ? `${region.full_domain}/locations/` : 'ramblas.io/locations/';
  };
  return (
    <ModalForm
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      isLoading={isCreatingLocation}
      isClosePopupOn={false}
      ignoreClickedState
    >
      {({ values }) => (
        <>
          <Form.GroupVertical label={t('createModal.nameLabel')} required>
            <Form.Input name="name" placeholder={t('createModal.namePlaceholder')} />
          </Form.GroupVertical>
          <Form.GroupVertical label={t('createModal.descriptionLabel')}>
            <Form.Input name="description" placeholder={t('createModal.descriptionPlaceholder')} />
          </Form.GroupVertical>
          <Form.GroupVertical label={t('createModal.regionLabel')} required>
            <Form.Input name="regionName" readOnly />
          </Form.GroupVertical>
          <Form.GroupVertical label={t('createModal.vanityUrlLabel')} required>
            <VanityUrlAvailabilityInput
              name="vanityUrl"
              placeholder={t('createModal.vanityUrlPlaceholder')}
              prepend={<PrependText>{buildDomain(values.regionUid)}</PrependText>}
            />
          </Form.GroupVertical>
          <Form.GroupVertical label={t('createModal.locationTrackingIdNameLabel')}>
            <Form.Input name="location_tracking_id" />
          </Form.GroupVertical>
          <OwnerInviteSection>
            <OwnerInviteHeader>
              <OwnerInviteTitle>{t('createModal.inviteOwnerTitle')}</OwnerInviteTitle>
              <OwnerInviteDescription>
                {t('createModal.inviteOwnerDescription')}
              </OwnerInviteDescription>
            </OwnerInviteHeader>
            <Form.GroupVertical label={t('createModal.inviteOwnerLabel')}>
              <InviteOwnerEmailSelect name="inviteOwnerEmail" />
            </Form.GroupVertical>
          </OwnerInviteSection>
        </>
      )}
    </ModalForm>
  );
};

const OwnerInviteSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #dee2e6;
`;

const OwnerInviteHeader = styled.div`
  margin-bottom: 16px;
  text-align: left;
`;

const OwnerInviteTitle = styled.h3`
  font-size: 16px;
  font-weight: bold;
`;

const OwnerInviteDescription = styled.p`
  margin: 0;
  color: #6c757d;
  font-size: 14px;
`;

const PrependText = styled.span`
  display: block;
  max-width: 140px;
  min-width: 0;
  flex: 0 1 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${media.sm`
    max-width: 180px;
    flex-basis: 180px;
  `}

  ${media.md`
    max-width: 200px;
    flex-basis: 200px;
  `}
`;

InviteOwnerEmailSelect.propTypes = {
  name: PropTypes.string.isRequired,
};

AddLocationForm.propTypes = {
  regions: PropTypes.arrayOf(
    PropTypes.shape({
      uid: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      full_domain: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default AddLocationForm;
